// SPDX-FileCopyrightText: 2026 Ben Richardson
// SPDX-License-Identifier: AGPL-3.0-or-later
//
// VIEW 7 — Three labels.
//
// THERE IS NO SINGLE AUSTRALIAN AIR QUALITY INDEX. New South Wales abolished its numeric AQI in
// November 2020 and now publishes five named categories and no number at all. Queensland still
// serves the classic index — concentration divided by the standard, times a hundred. The
// national enHealth scheme uses different particle breakpoints again.
//
// So the same microgram of smoke gets a different official word depending on which side of a
// border it was measured on. For one-hour PM2.5 the NSW "Good" ceiling is 25 µg/m³ and the
// enHealth one is 12.5 — a factor of two. A site that prints one national AQI is inventing it.
// This view puts all three scales on one axis and lets a reader drag a concentration across it.

import type { Bundle } from '../data';
import { loadLabels } from '../data';
import type { Labels } from '../types';
import { el, legend, panel, stat, statRow, xScroll } from '../components/ui';
import { concentration, number, percent, tip, UNIT } from '../format';
import { OFFICIAL_BAND_COLOUR } from '../palette';
import { glossaryLink } from '../glossary';

const BAND_NAMES = ['Good', 'Fair', 'Poor', 'Very poor', 'Extremely poor'];
const SYSTEM_ORDER = ['nsw', 'qld', 'enhealth'];
const METRIC = 'pm2.5-1h';
const MAX = 220;

function bandOf(bounds: number[], value: number): number {
  for (let i = 0; i < bounds.length; i++) {
    if (value <= bounds[i]) return i;
  }
  return bounds.length;
}

export async function renderLabels({ bundle, container }: { bundle: Bundle; container: HTMLElement }): Promise<void> {
  const labels: Labels = await loadLabels();

  const view = panel({
    title: 'Three labels for the same air',
    subtitle:
      'Australia has no single air quality index. New South Wales publishes named categories with no number, Queensland publishes the classic index, and the national health scheme uses different particle breakpoints again. Drag the marker to see where the same reading lands on each.',
  });

  const total = labels.disagreement.reduce((a, b) => a + b, 0);
  const disagreeing = total - (labels.disagreement[0] ?? 0);
  const twoOrMore = (labels.disagreement[2] ?? 0) + (labels.disagreement[3] ?? 0) + (labels.disagreement[4] ?? 0);

  view.body.append(
    statRow(
      stat(
        percent(disagreeing / total),
        'of measured hours',
        'get a different word from at least two of the three schemes',
        tip(`${number(disagreeing)} of ${number(total)} hours across the whole archive`)
      ),
      stat(
        percent(twoOrMore / total),
        'differ by two bands or more',
        'e.g. "Good" here and "Poor" over the border',
        tip('Two bands apart is the gap between "no action needed" and "sensitive groups should reduce outdoor activity".')
      ),
      stat('25', `NSW “Good” ceiling, ${UNIT}`, 'one-hour PM2.5'),
      stat('12.5', `enHealth “Good” ceiling, ${UNIT}`, 'exactly half the NSW figure')
    )
  );
  container.append(view.root);

  // ── The three scales, one axis ────────────────────────────────────────────
  const scales = panel({
    title: 'The three scales, drawn against one another',
    subtitle:
      'One horizontal axis of PM2.5 concentration, three strips. The breakpoints do not line up, and the disagreement is worst exactly where it matters — around the level at which health advice changes.',
  });

  const width = 720;
  const stripHeight = 34;
  const gap = 14;
  const labelWidth = 128;
  const height = SYSTEM_ORDER.length * (stripHeight + gap) + 52;

  const node = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
  node.setAttribute('viewBox', `0 0 ${width} ${height}`);
  node.setAttribute('class', 'chart');
  node.setAttribute('role', 'img');
  node.setAttribute('aria-label', 'Three official band scales for one-hour PM2.5');
  node.style.width = '100%';
  node.style.minWidth = '600px';
  node.style.height = 'auto';

  const make = (tag: string, attrs: Record<string, string | number>) => {
    const element = document.createElementNS('http://www.w3.org/2000/svg', tag);
    for (const [key, value] of Object.entries(attrs)) element.setAttribute(key, String(value));
    return element;
  };

  const x = (value: number) => labelWidth + (Math.min(value, MAX) / MAX) * (width - labelWidth - 16);

  SYSTEM_ORDER.forEach((key, row) => {
    const system = labels.systems[key];
    if (!system) return;
    const bounds = system.bands[METRIC] ?? [];
    const y = 26 + row * (stripHeight + gap);

    const name = make('text', { x: 0, y: y + stripHeight / 2 + 4, fill: 'var(--text-primary)', 'font-size': 11, 'font-weight': 620 });
    name.textContent = system.name.length > 22 ? `${system.name.slice(0, 21)}…` : system.name;
    name.setAttribute('data-tip', tip(system.name, system.note));
    node.append(name);

    for (let band = 0; band <= bounds.length; band++) {
      const from = band === 0 ? 0 : bounds[band - 1];
      const to = band === bounds.length ? MAX : bounds[band];
      const rect = make('rect', {
        x: x(from),
        y,
        width: Math.max(1, x(to) - x(from)),
        height: stripHeight,
        fill: OFFICIAL_BAND_COLOUR[band],
        stroke: 'var(--bg-surface)',
        'stroke-width': 1,
      });
      rect.setAttribute(
        'data-tip',
        tip(
          `${system.name}: ${BAND_NAMES[band]}`,
          `${concentration(from)} to ${band === bounds.length ? 'above' : concentration(to)} ${UNIT}`,
          system.note
        )
      );
      node.append(rect);
      if (x(to) - x(from) > 44) {
        const text = make('text', {
          x: (x(from) + x(to)) / 2,
          y: y + stripHeight / 2 + 4,
          'text-anchor': 'middle',
          'font-size': 9,
          fill: band >= 3 ? '#FFFFFF' : '#1A1310',
          'font-weight': 600,
        });
        text.textContent = BAND_NAMES[band];
        node.append(text);
      }
    }
  });

  for (const mark of [0, 25, 50, 100, 150, 200]) {
    const text = make('text', { x: x(mark), y: height - 22, 'text-anchor': 'middle', 'font-size': 9, fill: 'var(--text-tertiary)' });
    text.textContent = String(mark);
    node.append(text);
  }
  const axis = make('text', { x: labelWidth, y: height - 8, 'font-size': 9, fill: 'var(--text-tertiary)' });
  axis.textContent = `One-hour PM2.5, ${UNIT}`;
  node.append(axis);

  const marker = make('line', { x1: x(35), y1: 18, x2: x(35), y2: height - 30, stroke: 'var(--text-primary)', 'stroke-width': 2 });
  node.append(marker);

  scales.body.append(xScroll(node));

  const slider = el('div', 'scrubber');
  const range = el('input') as HTMLInputElement;
  range.type = 'range';
  range.min = '1';
  range.max = String(MAX);
  range.step = '1';
  range.value = '35';
  range.setAttribute('aria-label', 'PM2.5 concentration');
  const readout = el('span', 'scrubber-stamp');
  slider.append(range, readout);
  scales.body.append(slider);

  const verdict = el('p', 'note');
  scales.body.append(verdict);

  function update(): void {
    const value = Number(range.value);
    marker.setAttribute('x1', String(x(value)));
    marker.setAttribute('x2', String(x(value)));
    readout.textContent = `${concentration(value)} ${UNIT}`;
    const words = SYSTEM_ORDER.map((key) => {
      const system = labels.systems[key];
      const bounds = system?.bands[METRIC] ?? [];
      return { name: system?.name ?? key, band: bandOf(bounds, value) };
    });
    const spread = Math.max(...words.map((w) => w.band)) - Math.min(...words.map((w) => w.band));
    verdict.innerHTML =
      `An hour at <strong>${concentration(value)} ${UNIT}</strong> is ` +
      words.map((w) => `<strong>${BAND_NAMES[w.band]}</strong> under ${w.name}`).join(', ') +
      (spread === 0
        ? '. All three agree here.'
        : spread === 1
          ? '. One band apart — the schemes disagree, but adjacently.'
          : `. <strong>${spread} bands apart.</strong> The same air, measured by the same kind of instrument, described to the public in words that imply different actions.`);
  }
  range.addEventListener('input', update);
  update();

  scales.body.append(
    legend(
      BAND_NAMES.map((name, index) => ({
        label: name,
        hex: OFFICIAL_BAND_COLOUR[index],
        tip: 'The jurisdictions’ own published category colours — used in this view only, and nowhere else on the site',
      }))
    )
  );
  container.append(scales.root);

  // ── The distribution underneath ───────────────────────────────────────────
  const dist = panel({
    title: 'Where the hours actually fall',
    subtitle:
      'The whole archive of measured hourly PM2.5, in 2 µg/m³ bins. Almost every hour Australia has ever measured sits far to the left of every threshold — which is exactly why the annual average is such a poor description of the year.',
  });

  const histWidth = 720;
  const histHeight = 200;
  const hist = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
  hist.setAttribute('viewBox', `0 0 ${histWidth} ${histHeight}`);
  hist.setAttribute('class', 'chart');
  hist.setAttribute('role', 'img');
  hist.setAttribute('aria-label', 'Distribution of measured hourly PM2.5');
  hist.style.width = '100%';
  hist.style.minWidth = '520px';
  hist.style.height = 'auto';

  const bins = labels.counts.slice(0, 110);
  const maxCount = Math.max(...bins);
  const barW = (histWidth - 40) / bins.length;
  bins.forEach((count, index) => {
    // Square-root height: on a distribution this skewed a linear axis renders every bin above
    // 20 µg/m³ as a zero-height sliver and the tail — the entire point — disappears.
    const h = maxCount ? (Math.sqrt(count) / Math.sqrt(maxCount)) * (histHeight - 46) : 0;
    const value = index * labels.binWidth;
    const rect = make('rect', {
      x: 32 + index * barW,
      y: histHeight - 30 - h,
      width: Math.max(1, barW - 0.5),
      height: h,
      fill: OFFICIAL_BAND_COLOUR[bandOf(labels.systems.nsw?.bands[METRIC] ?? [], value)],
      'fill-opacity': 0.9,
    });
    rect.setAttribute(
      'data-tip',
      tip(
        `${value}–${value + labels.binWidth} ${UNIT}`,
        `${number(count)} hours (${percent(count / labels.totalHours, 2)} of the archive)`,
        `NSW calls this ${BAND_NAMES[bandOf(labels.systems.nsw?.bands[METRIC] ?? [], value)]}; enHealth calls it ${
          BAND_NAMES[bandOf(labels.systems.enhealth?.bands[METRIC] ?? [], value)]
        }`
      )
    );
    hist.append(rect);
  });
  for (const mark of [0, 25, 50, 100, 200]) {
    const px = 32 + (mark / labels.binWidth) * barW;
    if (px > histWidth - 8) continue;
    hist.append(make('line', { x1: px, y1: 8, x2: px, y2: histHeight - 30, stroke: 'var(--text-muted)', 'stroke-width': 0.8, 'stroke-dasharray': '3 3' }));
    const text = make('text', { x: px, y: histHeight - 16, 'text-anchor': 'middle', 'font-size': 9, fill: 'var(--text-tertiary)' });
    text.textContent = String(mark);
    hist.append(text);
  }
  const histAxis = make('text', { x: 32, y: histHeight - 3, 'font-size': 9, fill: 'var(--text-tertiary)' });
  histAxis.textContent = `Hourly PM2.5, ${UNIT} — bar height is the square root of the count, so the tail stays visible`;
  hist.append(histAxis);
  dist.body.append(xScroll(hist));

  dist.body.append(
    el(
      'p',
      'note',
      `${number(labels.totalHours)} measured hours across every station in the archive. The colours here are New South Wales' own category colours, used in this view only. ` +
        `Note what the shape means for an ${glossaryLink('aqi', 'index')}: because almost every hour is clean, an annual average sits deep inside the leftmost band no matter how bad the worst week was. ` +
        `The ${glossaryLink('who', 'WHO 2021 guideline')} for a 24-hour average is 15 ${UNIT}; Australia's standard is 25.`
    )
  );
  container.append(dist.root);

  void bundle;
}
